import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api';
import styles from '../../styles/GamesDataBrowser.module.css';

const RankingsDataBrowser = () => {
  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    season: '',
    week: '',
    poll: '',
    school: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });

  const fetchRankings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      });

      const response = await api.get(`/admin/rankings?${params}`);
      setRankings(response.data.rankings);
      setPagination(response.data.pagination);
      setError(null);
    } catch (err) {
      setError('Failed to fetch rankings data');
      console.error('Error fetching rankings:', err);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters]);

  useEffect(() => {
    fetchRankings();
  }, [fetchRankings]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const clearFilters = () => {
    setFilters({
      season: '',
      week: '',
      poll: '',
      school: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  if (loading) {
    return <div className={styles.loading}>Loading rankings...</div>;
  }

  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  return (
    <div className={styles.gamesDataBrowser}>
      <div className={styles.tabs}>
        <h3>Rankings Data Browser</h3>
      </div>

      {/* Filters */}
      <div className={styles.weekSelector}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
          <div>
            <label>Season:</label>
            <input
              type="number"
              placeholder="Season year..."
              value={filters.season}
              onChange={(e) => handleFilterChange('season', e.target.value)}
              style={{ width: '100%', padding: '8px', backgroundColor: '#3a3a3a', color: '#fff', border: '1px solid #555', borderRadius: '4px' }}
            />
          </div>
          <div>
            <label>Week:</label>
            <input
              type="number"
              placeholder="Week number..."
              value={filters.week}
              onChange={(e) => handleFilterChange('week', e.target.value)}
              style={{ width: '100%', padding: '8px', backgroundColor: '#3a3a3a', color: '#fff', border: '1px solid #555', borderRadius: '4px' }}
            />
          </div>
          <div>
            <label>Poll:</label>
            <input
              type="text"
              placeholder="Poll name..."
              value={filters.poll}
              onChange={(e) => handleFilterChange('poll', e.target.value)}
              style={{ width: '100%', padding: '8px', backgroundColor: '#3a3a3a', color: '#fff', border: '1px solid #555', borderRadius: '4px' }}
            />
          </div>
          <div>
            <label>School:</label>
            <input
              type="text"
              placeholder="School name..."
              value={filters.school}
              onChange={(e) => handleFilterChange('school', e.target.value)}
              style={{ width: '100%', padding: '8px', backgroundColor: '#3a3a3a', color: '#fff', border: '1px solid #555', borderRadius: '4px' }}
            />
          </div>
        </div>
        <button
          onClick={clearFilters}
          style={{ marginTop: '10px', padding: '8px 16px', backgroundColor: '#007bff', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
        >
          Clear Filters
        </button>
      </div>

      {/* Rankings Table */}
      <div className={styles.lockTable}>
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Season</th>
              <th>Week</th>
              <th>Poll</th>
              <th>Rank</th>
              <th>School</th>
              <th>Conference</th>
              <th>1st Place Votes</th>
              <th>Points</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {rankings.map(ranking => (
              <tr key={ranking.id}>
                <td>{ranking.id}</td>
                <td>{ranking.season}</td>
                <td>{ranking.week}</td>
                <td>{ranking.poll_name}</td>
                <td style={{ fontWeight: 'bold', color: ranking.rank <= 10 ? '#28a745' : ranking.rank <= 25 ? '#ffc107' : '#fff' }}>
                  #{ranking.rank}
                </td>
                <td>{ranking.school}</td>
                <td>{ranking.conference}</td>
                <td>{ranking.first_place_votes || 0}</td>
                <td>{ranking.points?.toLocaleString() || 0}</td>
                <td>{new Date(ranking.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        <div style={{ marginTop: '20px', textAlign: 'center', color: '#fff' }}>
          <span>
            Page {pagination.page} of {pagination.totalPages}
            ({pagination.total} total rankings)
          </span>
          <div style={{ marginTop: '10px' }}>
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page <= 1}
              style={{
                margin: '0 5px',
                padding: '5px 10px',
                backgroundColor: pagination.page <= 1 ? '#555' : '#007bff',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: pagination.page <= 1 ? 'not-allowed' : 'pointer'
              }}
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.totalPages}
              style={{
                margin: '0 5px',
                padding: '5px 10px',
                backgroundColor: pagination.page >= pagination.totalPages ? '#555' : '#007bff',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: pagination.page >= pagination.totalPages ? 'not-allowed' : 'pointer'
              }}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RankingsDataBrowser;
